import { usePreviousCompare } from 'cleveron-web-components/hooks';
import arrayMutators from 'final-form-arrays';
import { get as getNestedByKey, isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Form, FormSpy, useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';

class SubmissionError extends Error {
    constructor({ error, fields }) {
        super('SubmissionError');
        this.name = 'SubmissionError';
        this.error = error;
        this.fields = fields;
    }
}

const setSubmitErrors = ([errors = {}], state) => {
    Object.keys(state.fields).forEach(field => {
        if (errors[field]) {
            state.fields[field].data = {
                ...state.fields[field].data,
                submitError: errors[field],
            };
        } else {
            state.fields[field].data = {
                ...state.fields[field].data,
                submitError: null,
            };
        }
    });
};

const clearSubmitError = ([field], state) => {
    if (state.fields[field] && state.fields[field].data.submitError) {
        state.fields[field].data = {
            ...state.fields[field].data,
            submitError: null,
        };
    }
};

const untouchAllFields = (_, state) => {
    Object.keys(state.fields).forEach(field => {
        state.fields[field].touched = false;
    });
};

const handleSubmitFail = (error, dispatch, formApi) => {
    console.debug('error: ', error);

    if (error?.fields) {
        const registeredFields = formApi.getRegisteredFields();
        const { values } = formApi.getState();
        const errorFields = Object.keys(error.fields);
        const errorFieldsNotRendered = errorFields.filter(
            field =>
                !registeredFields.includes(field) ||
                Array.isArray(values[field] && !values[field].length)
        );

        let errorMessage;
        if (errorFields.length && errorFields.length === errorFieldsNotRendered.length) {
            // No field errors to show, display more info in snackbar
            const errorInfo = error.fields[errorFields[0]];
            errorMessage = {
                id: 'error.wrong_data_extra',
                values: {
                    extra: `${errorFields[0]}${
                        typeof errorInfo === 'string' ? ` ${errorInfo}` : ''
                    }`,
                },
            };
        }

        dispatch({
            payload: {
                message: errorMessage?.id ? errorMessage : { id: 'error.wrong_data' },
            },
            type: 'FORM_SUBMIT_FAILURE',
        });
    } else if (error?.message) {
        dispatch({
            payload: {
                message: error.message,
            },
            type: 'FORM_SUBMIT_FAILURE',
        });
    }

    return error;
};

const hasErrors = fieldErrors =>
    Object.values(fieldErrors || {}).some(error => error !== undefined && error !== null);

const useFormErrorFields = () => {
    const ref = useRef({ errorFields: [], fields: [], submitErrorFields: [] });
    const [fields, setFields] = useState([]);
    const form = useForm();
    useEffect(() => {
        const getAllFields = () =>
            form.getRegisteredFields().filter(
                (field, i, registeredFields) =>
                    // Omit parents of array fields (not actual fields)
                    !registeredFields.some(otherField => otherField.startsWith(`${field}[`))
            );
        const updateRef = ({
            errorFields = ref.current.errorFields,
            submitErrorFields = ref.current.submitErrorFields,
        }) => {
            ref.current.errorFields = errorFields;
            ref.current.submitErrorFields = submitErrorFields;
            const newFields = [...new Set([...errorFields, ...submitErrorFields])].sort();

            if (!isEqual(ref.current.fields, newFields)) {
                if (form.getState().submitFailed) {
                    ref.current.fields = newFields;
                    setFields(newFields);
                }
            }
        };

        const subscriptions = [
            // Overwrite submitErrorFields as soon as submitErrors changes (unless submitting)
            form.subscribe(
                ({ submitErrors }) => {
                    if (!form.getState().submitting && !form.getState().hasValidationErrors) {
                        const submitErrorFields = getAllFields().filter(field =>
                            // Error representation in obj { 'passwordPolicy.length': {} }
                            hasErrors(submitErrors?.[field])
                        );
                        updateRef({ submitErrorFields });
                    }
                },
                { submitErrors: true }
            ),
            // Overwrite errorFields as soon as errors changes
            form.subscribe(
                ({ errors }) => {
                    const errorFields = getAllFields().filter(field =>
                        // Error representation in obj { passwordPolicy: { length: {} } }
                        hasErrors(getNestedByKey(errors, field))
                    );
                    updateRef({ errorFields });
                },
                { errors: true }
            ),
            // Remove field from submitErrorFields as soon as it first becomes dirty
            form.subscribe(
                ({ dirtyFieldsSinceLastSubmit }) => {
                    const submitErrorFields = ref.current.submitErrorFields.filter(
                        field => !dirtyFieldsSinceLastSubmit[field]
                    );
                    updateRef({ submitErrorFields });
                },
                { dirtyFieldsSinceLastSubmit: true }
            ),
        ];

        return () => {
            subscriptions.forEach(unsubscribe => unsubscribe());
        };
    }, [form]);

    return fields;
};

const ReactFinalForm = ({
    children,
    componentProps,
    onChanges = () => {},
    onSubmit,
    onSubmitFail = () => {},
    onSubmitSuccess = () => {},
    subscription = {},
    formRef,
    ...props
}) => {
    // To enable "nested" onX props - outer can be accessed from inner handler
    const nestedProps = { ...componentProps, ...props };

    const dispatch = useDispatch();
    if (Object.keys(subscription).some(key => key === 'values')) {
        console.warn(
            'Using values in subscription will cause re-renders on every input change, use <FormSpy /> or useFormValues() instead!'
        );
    }
    return (
        <Form
            {...props}
            initialValuesEqual={isEqual}
            mutators={{ ...arrayMutators, clearSubmitError, setSubmitErrors, untouchAllFields }}
            onSubmit={async (values, formApi) => {
                const { mutators } = formApi;
                try {
                    const result = await onSubmit(values, dispatch, formApi, nestedProps);
                    mutators.setSubmitErrors({});
                    onSubmitSuccess(result, dispatch, formApi, nestedProps, values);
                    mutators.untouchAllFields();
                } catch (err) {
                    if (err instanceof SubmissionError) {
                        onSubmitFail(err, dispatch, formApi, nestedProps);
                        // NB! Default submitErrors are cleared on next submit
                        mutators.setSubmitErrors(err.fields);
                        return err.fields;
                    }
                    onSubmitFail(err, dispatch, formApi, nestedProps);
                }
                return undefined;
            }}
            subscription={{ pristine: true, submitting: true, ...props, ...subscription }}
            validateOnBlur={false}
        >
            {({ handleSubmit, submitting, form }) => {
                if (formRef) {
                    formRef.current = form;
                }
                return (
                    <>
                        <FormSpy
                            onChange={({
                                values,
                                dirtyFieldsSinceLastSubmit,
                                submitErrors,
                                modified,
                            }) => {
                                if (dirtyFieldsSinceLastSubmit && submitErrors) {
                                    Object.keys(dirtyFieldsSinceLastSubmit).forEach(field =>
                                        form.mutators.clearSubmitError(field)
                                    );
                                }
                                onChanges(
                                    {
                                        change: form.change,
                                        modifiedValues: modified || {},
                                        values,
                                    },
                                    dispatch,
                                    form,
                                    nestedProps
                                );
                            }}
                            subscription={{
                                dirtyFieldsSinceLastSubmit: true,
                                modified: true,
                                submitErrors: true,
                                submitting: true,
                                values: true,
                            }}
                        />
                        {children({
                            ...props,
                            handleSubmit: e => {
                                if (form.getState().hasValidationErrors) {
                                    const { errors } = form.getState();
                                    onSubmitFail(
                                        { ...e, fields: errors },
                                        dispatch,
                                        form,
                                        nestedProps
                                    );
                                }
                                return handleSubmit(e);
                            },
                            submitting,
                        })}
                    </>
                );
            }}
        </Form>
    );
};

const useFormValuesPrev = (fields = [], { ignoreFocused } = {}) => {
    const isArray = Array.isArray(fields);
    if (fields === null || !(isArray || typeof fields === 'object')) {
        throw new Error('fields have to by array or an object');
    }
    // eslint-disable-next-line no-param-reassign
    fields = usePreviousCompare(fields);
    const valuesRef = useRef([{}, {}]);
    const keys = usePreviousCompare(isArray ? fields : Object.values(fields));
    const form = useForm();
    const formInitialValues = form.getState().initialValues;
    const initialValues = keys.reduce(
        (acc, field) => ({ ...acc, [field]: getNestedByKey(formInitialValues, field) }),
        {}
    );
    const [values, setValues] = useState([initialValues, initialValues]);
    useEffect(() => {
        const unsubscribe = form.subscribe(
            ({ active, values: formValues }) => {
                if (ignoreFocused && active && keys.includes(active)) {
                    return;
                }

                const changedValues = isArray
                    ? fields.reduce(
                          (acc, field) => ({ ...acc, [field]: getNestedByKey(formValues, field) }),
                          {}
                      )
                    : Object.entries(fields).reduce(
                          (acc, [key, field]) => ({
                              ...acc,
                              [key]: getNestedByKey(formValues, field),
                          }),
                          {}
                      );
                if (!isEqual(valuesRef.current, changedValues)) {
                    setValues([changedValues, valuesRef.current]);
                    valuesRef.current = changedValues;
                }
            },
            { active: true, values: true }
        );

        return () => {
            unsubscribe();
        };
    }, [fields, form, ignoreFocused, isArray, keys]);
    return values;
};

const useFormValues = (fields = [], opts) => {
    const [values] = useFormValuesPrev(fields, opts);
    return values;
};

const reactFinalFormHOC =
    ({ ...formProps } = {}) =>
    FormComponent =>
    // eslint-disable-next-line react/display-name
    ({ ...componentProps }) => {
        return (
            <ReactFinalForm componentProps={componentProps} {...componentProps} {...formProps}>
                {({ ...reactFinalFormProps }) => <FormComponent {...reactFinalFormProps} />}
            </ReactFinalForm>
        );
    };

export {
    handleSubmitFail,
    ReactFinalForm,
    reactFinalFormHOC,
    SubmissionError,
    useFormErrorFields,
    useFormValues,
    useFormValuesPrev,
};
